import { COUNTRIES, PAYMENT_GATEWAY } from "../../core/constants/constants";
import * as $bootstrapHelper from '../../core/helpers/bootstrap.helper';
import * as $cartHelper from '../../core/helpers/cart.helper';
import * as $numberHelper from '../../core/helpers/number.helper';
import { getEventTrackingCodesByAction, waitForFbq, waitForTtq } from "../../core/helpers/tracking-scripts.helper";
import { CheckoutBookingModel } from "../../core/models/booking.model";
import { CartModel, TicketModel } from "../../core/models/cart.model";
import { CheckoutConfig, getCheckoutConfig } from "../../core/models/checkout-config.model";
import { CheckoutPayload } from "../../core/models/checkout-payload.model";
import { CreditCardModel } from "../../core/models/credit-card.model";
import { EnvModel, getEnvModel } from "../../core/models/env.model";
import { getGlobalConfig, GlobalConfig } from "../../core/models/global-config.model";
import { PaymentGatewayModel } from "../../core/models/payment-gateway.model";
import { PaymentGatewayGroupModel } from "../../core/models/payment-group.model";
import { SurchargeModel } from "../../core/models/surcharge.model";
import { TermsAndConditionsModel } from "../../core/models/terms-and-conditions.model";
import { EventTrackingCode } from "../../core/models/tracking-code.model";
import { getUiStrings, UiStringModel } from "../../core/models/ui-string.model";
import { UserBookingDetailsModel } from "../../core/models/user-booking-details.model";
import DataService from "../../core/services/data.service";
import UrlService from "../../core/services/url.service";
import { CheckoutForm } from "./booking-form.const";
import { CheckoutConnexPoint } from "./payments/connex-point.const";
import { CheckoutFatZebra } from "./payments/fat-zebra.const";
import { CheckoutPayPal } from "./payments/paypal.const";
import { CheckoutStripeWallet } from "./payments/stripe-wallet.const";
import { CheckoutStripe } from "./payments/stripe.const";
import { CheckoutCustomTest } from "./payments/test.const";

declare global {  
  var gtag: undefined | any;
  var ttq: undefined | any;
}

export const enum FeeType {
    GooglePaySurcharge = 14,
    ApplePaySurcharge = 15
}

export class CheckoutViewModel {
  public readonly connexPoint: CheckoutConnexPoint;
  public readonly fatZebra: CheckoutFatZebra;
  public readonly test: CheckoutCustomTest;
  public readonly paypal: CheckoutPayPal;
  public readonly stripe: CheckoutStripe;
  public readonly form: CheckoutForm;

  public readonly isWidget: boolean;
  public readonly currency: string;
  public readonly uiStrings: UiStringModel;
  public readonly urlService: UrlService;
  public readonly hasPaymentOptions = ko.observable(true);
  public readonly paymentGatewayId = ko.observable(0);
  public readonly cardPaymentTitle = ko.observable('');
  public readonly isShowCarousel = ko.observable(false);
  public readonly acknowledgedTerms = ko.observable(true); // TO DO: false for UK
  public readonly isLoading = ko.observable(true);
  public readonly disablePurchase = ko.observable(false);
  public readonly emailCorrection?: KnockoutObservable<{correction:string, misspelling: string}> = ko.observable();


  // WIDGETS
  private readonly widgetType: string;
  private readonly widgetEventId: number;
  private readonly isEventListingWidget: boolean;
  private cardBrand?: string;

  // SERVICES
  private readonly dataService: DataService;
  private readonly envSetting: EnvModel;
  private readonly config: GlobalConfig;
  private readonly checkout: CheckoutConfig;


  // APPLE/GOOGLE PAY
  public readonly googleApplePaymentTitle = ko.observable('');
  private readonly googleApplePaymentGatewayId = ko.observable(0);
  private readonly googleApplePaymentGatewayIsActive = ko.observable(false);
  private readonly googleApplePaymentGatewayApiTypeName = ko.observable('');
  private readonly googlePaySurchargePercentage = ko.observable(0);
  private readonly applePaySurchargePercentage = ko.observable(0);

  public isPayWithCreditCardSelected = ko.observable(true);
  public isPayWithGoogleSelected = ko.observable(false);
  public isPayWithApplePaySelected = ko.observable(false);
  public isPayWithGPayVisible = ko.observable(false);
  public isPayWithApplePayVisible = ko.observable(false);

  
  private readonly cardPaymentGatewayApiTypeName = ko.observable('');
  // cardPaymentTypeAllowed default true because card type checking only done for stripe
  private readonly cardPaymentTypeAllowed = ko.observable(true);

  private readonly cardPaymentGatewayId = ko.observable(0);
  private cardPaymentGateway: PaymentGatewayModel;
  private readonly cardPaymentGatewayIsActive = ko.observable(false);
  private readonly expirySpelling = ko.observable('');

  private readonly requireRecaptchaOnFreeBookings = ko.observable(false);
  private readonly requireRecaptchaOnPaidBookings = ko.observable(false);
  private readonly recaptchaOnBookingsAlwaysOff = ko.observable(false);
  private readonly isRecaptchaOffOlderAccount = ko.observable(false);

  // ERROR MESSAGES
  public readonly showErrorMessage = ko.observable(false);
  public readonly errorMessage = ko.observable<string | undefined>(undefined);
  public readonly hasTermsError = ko.observable(false);
  public readonly termsErrorMessage = ko.observable('&nbsp;'); // consider using css visibility

  //LOOKUPS
  public readonly availableCountries = ko.observable(COUNTRIES);
  private domesticCardCountries: string[] = [];

  // TRACKING
  public trackingTag = ko.observable('');
  public customId = ko.observable('');
  private readonly shouldFBTrackCheckoutView = ko.observable(false);
  private readonly shouldFBTrackSubmitPayment = ko.observable(false);
  private readonly shouldTikTokTrackCheckoutView = ko.observable(false);
  private readonly shouldTikTokTrackSubmitPayment = ko.observable(false);
  private readonly shouldGATrackCheckoutView = ko.observable(false);
  private readonly shouldGATrackSubmitPayment = ko.observable(false);
  private readonly googleTrackingCodes = ko.observableArray<EventTrackingCode>([]);

  // TERMS ACKNOWLEDGEMENT
  public eventTerms = ko.observableArray<TermsAndConditionsModel>([]);

  // GENERIC PAYMENT
  private creditCard: CreditCardModel;

  private _paymentRequestType?: string;
  public accountId = ko.observable(0);

  // SURCHARGES
  private readonly surcharges: KnockoutObservable<SurchargeModel[]>;

  private isForeignCardSurchargeEnabled: boolean = false;
  private totalFCS = ko.observable(''); // TO DO: consider changing property name
  private totalAmountIncludingFCS = ko.observable('');
  public surchargeApplied = ko.observable(0);
  public surchargeCardType = ko.observable();

  // SHOPPING CART
  public shoppingCartVm = ko.observable<CartModel>();
  public shoppingCartTotalAmount = ko.observable(0);
  public shoppingCartTotalAmountWithSurcharge = ko.observable(0);
  public cartTicketCount = ko.observable(0);
  public methods: KnockoutObservable<any>;
  public links = ko.observable({});
  public isDataCollectionComplete = ko.observable(false);
  public readonly accountName = ko.pureComputed(() => this.shoppingCartVm()?.accountName);

  private shoppingCartLastEventId = ko.observable(0);
  private paymentGatewayGroup: PaymentGatewayGroupModel;

  // QUICKSALE
  public quickSaleId = ko.observable('');
  private readonly allEventsQuickSaleEnabled = ko.observable(false);
  private readonly isCardSwipeEnabled = ko.observable(false);
  private readonly isCardSwipeEnabledForAll = ko.observable(false);
  private readonly isQuickSaleIdValid = ko.observable(false);

  // BOX OFFICE
  private readonly bookingHasBoxOfficeFee = ko.observable(false);

  // PURE COMPUTED  
  public isQuickSaleBooking: KnockoutComputed<boolean>;
  public isCheckoutDetailsVisible: KnockoutComputed<boolean>;
  public isPaymentOptionsVisible: KnockoutComputed<boolean>;
  
  public shouldShowBookingDetails: KnockoutComputed<boolean>;
  public showCardDetails: KnockoutComputed<boolean>;
  public totalAmountPlusSurcharge: KnockoutComputed<string>; // TO DO: consider variable rename
  public shouldShowAcknowledgement: KnockoutObservable<boolean>;
  public enableEventTermsLink: KnockoutComputed<boolean>;
  public shouldShowRememberBookingDetails: KnockoutComputed<boolean>;
  public paymentRequestButtonDisabled: KnockoutComputed<boolean>;
  public disableAlternativePayments: KnockoutComputed<boolean>;

  public hasTickets = ko.pureComputed(() => (this.shoppingCartVm()?.tickets?.length ?? 0) > 0);
  public stickyFooterHeight = ko.pureComputed(() => {
      const minheight = 69;
      if (this.hasTickets() === undefined) return `${minheight}px`;

      const height = this.getStickyFooterHeight();
      return Math.max(height, minheight) + 'px';
  });

  public stickyTotalAmount = ko.pureComputed(() => {
    const amt = this.shoppingCartTotalAmountWithSurcharge();
    if (amt === 0) {
        return "Free";
    }

    return `${this.uiStrings.currency} ${amt.toFixed(2)}`;
  });


  private allowedCardNetworks: google.payments.api.CardNetwork[] = ["AMEX", "MASTERCARD", "VISA"];
  private allowedCardAuthMethods: google.payments.api.CardAuthMethod[] = ["PAN_ONLY"];

  private tokenizationSpecification: google.payments.api.PaymentMethodTokenizationSpecification = {
    type: 'PAYMENT_GATEWAY',
    parameters: {
      'gateway': 'fatzebra',
      'gatewayMerchantId': ''
    }
  };        
  private billingAddressParameters: google.payments.api.BillingAddressParameters = {
    phoneNumberRequired: true,
    format: 'FULL'
  };
  private paymentsClient?: google.payments.api.PaymentsClient;

  private paymentRequestSelected: KnockoutComputed<boolean>;

  constructor() {
    this.envSetting = getEnvModel();
    this.config = getGlobalConfig();
    this.urlService = new UrlService(this.envSetting);
    this.dataService = new DataService(this.envSetting);
    this.uiStrings = getUiStrings();
    this.expirySpelling(this.config.uiStrings.expirySpelling);

    this.requireRecaptchaOnFreeBookings(this.config.config.requireRecaptchaOnFreeBookings);
    this.requireRecaptchaOnPaidBookings(this.config.config.requireRecaptchaOnPaidBookings);
    this.disableAlternativePayments = ko.pureComputed(() => !this.isDataCollectionComplete() || this.bookingHasBoxOfficeFee() || this.isQuickSaleBooking())

    this.form = new CheckoutForm(this.dataService, (error: string) => this.setErrorMessage(error))

    this.initEmailCorrectionSuggestion();
    
    this.paypal = new CheckoutPayPal(
      this.paymentGatewayId, 
      this.shoppingCartTotalAmount,
      this.isLoading,
      this.disableAlternativePayments,
      (error: string) => this.setErrorMessage(error),
      () => this.onSelectPayWithCreditCard(),
      this.uiStrings,
      this.form,
    );
    this.fatZebra = new CheckoutFatZebra(
      this.paymentGatewayId, 
      this.shoppingCartTotalAmount,
      (error: string) => this.setErrorMessage(error),
      (card: CreditCardModel) => this.processCardPayment(card),
      this.uiStrings,
      getGlobalConfig(),
      this.dataService
    );

    this.test = new CheckoutCustomTest(
      (paymentType: string, data: Partial<CheckoutPayload>) => this.processPayment(paymentType, data),
    );
    this.connexPoint = new CheckoutConnexPoint(
      this.config,
      this.isCardSwipeEnabled,
      ko.pureComputed(() => `${this.form.bookingFirstName()} ${this.form.bookingLastName()}`),
      ko.pureComputed(() => `${this.form.bookingPostcode()}`),
      (error: string) => this.setErrorMessage(error),
      (paymentType: string, data: Partial<CheckoutPayload>) => this.processPayment(paymentType, data),
      (cardBrand: string, cardNumberId: string, cardBrandId: string, surchargeId: string) => this.updateCardBrand(cardBrand, cardNumberId, cardBrandId, surchargeId),
      this.dataService
    );
    
    // Checkout constants

    this.checkout = getCheckoutConfig()!;
    this.widgetType = this.checkout.widgetType;
    this.widgetEventId = this.checkout.widgetEventId;
    this.isEventListingWidget = this.checkout.isEventListingWidget;
    this.shouldShowAcknowledgement = ko.observable(this.checkout.isInEU);
    this.isWidget = this.checkout.isWidget || this.isEventListingWidget;
    this.currency = this.uiStrings.currency;
    this.isShowCarousel(this.checkout.showCarousel);
    this.isCardSwipeEnabledForAll(this.checkout.isCardSwipeEnabledForAll);

    if (this.checkout.isInEU) {      
      this.form.hasPermissionToContact(false);
      this.acknowledgedTerms(false);
      this.form.rememberMyDetails(false);
    }

    // INIT VALUES

    this.methods = ko.observable({
      applyPromoCode: (model: any) => this.dataService.ApplyGcOrPromoCode(model),
      applyBoxOfficeFee: (model: any) => this.dataService.ApplyBoxOfficeFee(model),
      checkHasValidPromoCodes: (model: any) => this.dataService.CheckHasValidPromoCodes(model),
      removeTicketItem: (model: any) => this.dataService.RemoveTicketItem(model),
      removeDonationItem: (model: any) => this.dataService.RemoveDonationItem(model),
      removeGCBookingItem: (model: any) => this.dataService.RemoveGCBookingItem(model),
      onNoCartItems: (eventId: number, redirectToGc: boolean) => this.onNoCartItems(eventId, redirectToGc),
      onBookingTicketRemove: (sData: any) => this.form.updatePhoneVisibility(sData),
      getAccountHasOtherEvents: (aid: number, eid: number) => this.dataService.GetAccountHasOtherEvents(aid, eid),
      getAccountHasGcs: (aid: number) => this.dataService.GetAccountHasGcs(aid)
    });

    this.InitPureComputed();

    this.surcharges = ko.observableArray([]);
    // INIT

    let timeout: NodeJS.Timeout;
    window.addEventListener('resize', () => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
            const el = document.querySelector<HTMLDivElement>('.sticky-footer-offset');
            if (el) {
                el.style.height = this.getStickyFooterHeight() + "px";
            }
        }, 200);
    });
    this.dataService.GetShoppingCart().then((cart: CartModel) => this.initialiseCheckout(cart), () => this.onNoCartItems());

    const stripeWallet = new CheckoutStripeWallet(
      this.config,
      this.uiStrings,
      this.shoppingCartTotalAmountWithSurcharge,
      this.isPayWithApplePayVisible,
      this.isPayWithGPayVisible,
      this.form.bookingEmail,
      (paymentType: string, partialPayload?: Partial<CheckoutPayload>, userDetails?: UserBookingDetailsModel) => this.processPayment(paymentType, partialPayload, userDetails),
      (cardToken: string, ev: stripe.paymentRequest.StripePaymentMethodPaymentResponse) => this.processStripePayment(cardToken, ev),
    );

    this.stripe = new CheckoutStripe(
      this.config,
      this.cardPaymentTypeAllowed,
      (error: string) => this.setErrorMessage(error),
      (card: CreditCardModel) => this.processCardPayment(card),
      (cardBrand: string, cardNumberId: string, cardBrandId: string, surchargeId: string) => this.updateCardBrand(cardBrand, cardNumberId, cardBrandId, surchargeId),
      stripeWallet
    );
  }

  private async initialiseCheckout(data: CartModel) {
    if ((data.tickets === null || data.tickets.length === 0) && (data.giftCertificates === null || data.giftCertificates.length === 0)) {
        this.onNoCartItems();
        return;
    }
    this.accountId($cartHelper.GetAccountIdFromCart(data));
    this.shoppingCartLastEventId($cartHelper.GetLastEvent(data));
    this.bookingHasBoxOfficeFee(data.boxOfficeFees !== null && data.boxOfficeFees.length > 0);
    this.shoppingCartVm(data);  
    this.form.updatePhoneVisibility(data);

    this.links($cartHelper.GetCartLinks(
      this.envSetting.bookingSitePath,
      this.envSetting.eventSitePath,
      this.accountId(),
      this.shoppingCartLastEventId(),
      this.widgetType,
      this.widgetEventId,
      this.isEventListingWidget,
      this.urlService.GetActiveQueryString())
    );
    this.sendCartTrackingDetails($cartHelper.GetEventIds(data));


    this.dataService.GetSurcharges(this.accountId()).then((surcharges: SurchargeModel[]) => {
      this.paypal.applySurcharge(surcharges);
      this.surcharges(surcharges);

      var googlePaySurcharge = surcharges.find(x => x.feeTypeId == FeeType.GooglePaySurcharge);
      if (googlePaySurcharge?.percentage > 0) {
          this.googlePaySurchargePercentage(googlePaySurcharge.percentage);
      }

      var applePaySurcharge = surcharges.find(x => x.feeTypeId == FeeType.ApplePaySurcharge);
        if (applePaySurcharge?.percentage > 0) {
            this.applePaySurchargePercentage(applePaySurcharge.percentage);
      }
    });

    if (this.quickSaleId()) {
      this.dataService.ValidateQuickSaleId(this.accountId(), this.quickSaleId())
          .then((res: any) => this.isQuickSaleIdValid(res.isValid));
    }

    if (data.tickets && data.tickets.length > 0) {
        this.allEventsQuickSaleEnabled(data.tickets.every((t: TicketModel) => t.event.isQuickSaleEnable));
        this.isCardSwipeEnabled(this.isCardSwipeEnabledForAll() && data.tickets.every((t: TicketModel) => t.event.isCardSwipeEnabled));
    }

    this.dataService.GetAccountRecaptchaSettings(this.accountId())
        .then((settings: any) => {
            this.recaptchaOnBookingsAlwaysOff(settings.recaptchaAlwaysOff);
            this.isRecaptchaOffOlderAccount(settings.recaptchaOffOnOldAccount);

            if (!this.recaptchaOnBookingsAlwaysOff() && !this.isRecaptchaOffOlderAccount()) {
                if (this.requireRecaptchaOnFreeBookings() || this.requireRecaptchaOnPaidBookings()) {
                    this.initRecaptcha();
                }
            }
        })
        .fail(() => {
            if (this.requireRecaptchaOnFreeBookings() || this.requireRecaptchaOnPaidBookings()) {
                this.initRecaptcha();
            }
        });

    if (this.shoppingCartTotalAmount() === 0) { // If front end considers this a free booking, no need to load payment gateways.
        this.isLoading(false); // Back-end validation still occurs.
        this.disablePurchase(false);
        return;
    }
    const paymentGatewayGroup = await this.dataService.GetPaymentGroup(this.accountId());

    this.paymentGatewayGroup = paymentGatewayGroup;
  
    var cardPaymentGateway = paymentGatewayGroup.cardPaymentGateway;
    var googleApplePaymentGateway = paymentGatewayGroup.googleApplePaymentGateway;
    var paypalGateway = paymentGatewayGroup.payPalPaymentGateway; 

    this.hasPaymentOptions(paymentGatewayGroup.cardPaymentGateway.isActive || paymentGatewayGroup.googleApplePaymentGateway.isActive || paymentGatewayGroup.payPalPaymentGateway.isActive);
      
    this.googleApplePaymentGatewayApiTypeName(googleApplePaymentGateway.apiTypeName);
    this.googleApplePaymentGatewayId(googleApplePaymentGateway.paymentGatewayId);
    this.googleApplePaymentGatewayIsActive(googleApplePaymentGateway.isActive);

    this.cardPaymentGatewayApiTypeName(cardPaymentGateway.apiTypeName);
    this.cardPaymentGatewayId(cardPaymentGateway.paymentGatewayId);
    this.cardPaymentGatewayIsActive(cardPaymentGateway.isActive);

    if (!this.disableAlternativePayments() && paypalGateway.isActive && paypalGateway.apiTypeName === PAYMENT_GATEWAY.BRAINTREE_PAYPAL) {
        this.paypal.enabled(true); // enable spinner
        this.dataService.GetBrainTreeToken(paymentGatewayGroup.payPalPaymentGateway.paymentGatewayId).then((result: any) => {
            paymentGatewayGroup.payPalPaymentGateway.paymentGatewayToken = result;
            this.paypal.initialise(paymentGatewayGroup.payPalPaymentGateway);
        });
    }
    this.stripe.initialise(cardPaymentGateway, googleApplePaymentGateway);

    
    if (googleApplePaymentGateway.apiTypeName === PAYMENT_GATEWAY.FATZEBRA) {
        this.shoppingCartTotalAmount.subscribe((amount: number) => {
            if (amount === 0) {
                this.onSelectPayWithCreditCard();
            }
        });
        (this.tokenizationSpecification as any).parameters['gatewayMerchantId'] = googleApplePaymentGateway.googlePayMerchantId;

        $.getScript(this.config.config.googlePayApiUrl, () => {
            this.onGooglePayLoaded();
        });

        $.getScript(this.config.config.applePayApiUrl, () => {
            let applePaySession = (window as any).ApplePaySession;
            if (applePaySession) {
                if (applePaySession.canMakePayments()) {
                    this.isPayWithApplePayVisible(true);
                }
            }
        });
    }

      this.disablePurchase(false);

    switch (cardPaymentGateway.apiTypeName) {
        case PAYMENT_GATEWAY.FATZEBRA:
            if (cardPaymentGateway.clickToPayEnabled === false) {
                this.fatZebra.initialise(cardPaymentGateway, "", this);
            }
            else {
                $("#fz-clicktopaymessage").css("display", "block");
                this.disablePurchase(true);
                this.cardPaymentGateway = cardPaymentGateway;
                this.onEmailBlur();
            }
            break;
        case PAYMENT_GATEWAY.CONNEXPOINT:
            this.connexPoint.initialise(cardPaymentGateway);
            break;
        case PAYMENT_GATEWAY.TEST:
            this.test.initialise()
            break;
    }
    if (!cardPaymentGateway.isActive) {
        this.cardPaymentTitle('This payment method is temporarily unavailable');
    }
    if (!googleApplePaymentGateway.isActive) {
        this.googleApplePaymentTitle('This payment method is temporarily unavailable');
    }
    if (cardPaymentGateway) {
        this.domesticCardCountries = cardPaymentGateway.domesticCardCountries;
        this.isForeignCardSurchargeEnabled = cardPaymentGateway.isForeignCardSurchargeEnabled;
        if (!cardPaymentGateway.isActive) {
            $('#cc-btn').removeClass('active');
            //$('#cc-btn').addClass('disabled');
            $('#cc-btn').prop('disabled', true)
        } else {
            //$('#cc-btn').removeClass('disabled');
            $('#cc-btn').prop('disabled', false)
        }
    }

      this.isLoading(false);

  }

    public onEmailBlur() {
        if (this.form.bookingEmail.isValid() && this.form.bookingEmail() !== "" && typeof (this.cardPaymentGateway) !== 'undefined') {
            this.fatZebra.initialise(this.cardPaymentGateway, this.form.bookingEmail(), this);
        }
    }

  private getStickyFooterHeight(): number {
    return document.querySelector<HTMLDivElement>('.bottom-anchored-botton')?.offsetHeight ?? 69;
  }

  private InitPureComputed() {
      this.isQuickSaleBooking = ko.pureComputed(() => this.quickSaleId().length > 0 && this.isQuickSaleIdValid() && (this.allEventsQuickSaleEnabled() || this.isCardSwipeEnabled()));

      this.isCheckoutDetailsVisible = ko.pureComputed(() => this.hasPaymentOptions() && this.shoppingCartVm() !== null);

      this.isPaymentOptionsVisible = ko.pureComputed(() =>
          this.shoppingCartTotalAmount() > 0
          && (this.paypal.ready() || this.isPayWithGPayVisible() || this.isPayWithApplePayVisible()));

      this.shouldShowBookingDetails = ko.pureComputed(() => this.isPayWithCreditCardSelected());

      this.showCardDetails = ko.pureComputed(() => (this.shouldShowBookingDetails() && this.shoppingCartTotalAmount() > 0) && this.cardPaymentGatewayIsActive());

      this.totalAmountPlusSurcharge = ko.pureComputed(() => {
          const tAmount = this.shoppingCartTotalAmount() * (1 + (this.surchargeApplied() / 100));
          return $numberHelper.ToCurrency(tAmount);
      });

      this.shoppingCartTotalAmountWithSurcharge = ko.pureComputed(() => {
          const amount = this.shoppingCartTotalAmount() * (1 + (this.surchargeApplied() / 100));
          return $numberHelper.Round($numberHelper.Round(amount, 4), 2);
                /* round to 4 decimal places first to correct any discrepancy from Javascript for computed float values before rounding to 2
                    Sample data: shoppingCartTotalAmount is 84AUD. surchargeApplied is 0.5
                        amount = 84 * (1 + 0.005) = 81.40499999999999 in JS but should have been 81.405
                        if we round that directly to 2 decimal places, amount = 81.40 instead of 81.41
                */
      });

      this.enableEventTermsLink = ko.pureComputed(() => (this.shoppingCartVm()?.tickets?.length ?? 0) > 0);
      this.shouldShowRememberBookingDetails = ko.pureComputed(() => !this.paymentRequestSelected());
      this.paymentRequestButtonDisabled = ko.pureComputed(() => this.disableAlternativePayments() || !this.googleApplePaymentGatewayIsActive());
      this.paymentRequestSelected = ko.pureComputed(() => this.isPayWithGoogleSelected() || this.isPayWithApplePaySelected());
  }


    public getGoogleIsReadyToPayRequest(): google.payments.api.IsReadyToPayRequest {
      return {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [this.getGooglePayPaymentMethod()]
      };
    }

    public getGooglePayPaymentMethod(): google.payments.api.IsReadyToPayPaymentMethodSpecification {
      return {
        type: 'CARD',
        parameters: {
          allowedAuthMethods: this.allowedCardAuthMethods,
          allowedCardNetworks: this.allowedCardNetworks,
          billingAddressRequired: true,
          billingAddressParameters: this.billingAddressParameters
        }
      };
    }
  
    public getGooglePaymentDataRequest(): google.payments.api.PaymentDataRequest {
        return {
          apiVersion: 2,
          apiVersionMinor: 0,
          transactionInfo: this.getGoogleTransactionInfo(),
          merchantInfo: {
            // todo a merchant ID is available for a production environment after approval by Google
            // See {link https://developers.google.com/pay/api/web/guides/test-and-deploy/integration-checklist|Integration checklist}
            merchantId: 'BCR2DN4T6C25XDZ7',
            merchantName: 'TryBooking'
          },
          emailRequired: true,
          allowedPaymentMethods: [{
            type: 'CARD',
            tokenizationSpecification: this.tokenizationSpecification,
            parameters: {
              allowedAuthMethods: this.allowedCardAuthMethods,
              allowedCardNetworks: this.allowedCardNetworks,
              billingAddressRequired: true,
              billingAddressParameters: this.billingAddressParameters
            }
          }]
        };
    }

    public getGooglePaymentsClient() {
        if (!this.paymentsClient) {
            this.paymentsClient = new google.payments.api.PaymentsClient({ environment: this.envSetting.googlePayEnvironment as any }); //PRODUCTION
        }
        return this.paymentsClient;
    }

    public onGooglePayLoaded() {
        let applePaySession = (window as any).ApplePaySession;
        if (applePaySession) {
            return;
        }
        const paymentsClient = this.getGooglePaymentsClient();

        paymentsClient.isReadyToPay(this.getGoogleIsReadyToPayRequest())
            .then((response: any) =>  {
                if (response.result) {
                    this.addGooglePayButton();
                    this.isPayWithGPayVisible(true);
                    document.getElementById('googlepaycontainer2')?.classList.remove("buy-with-gpay-btn");
                    document.getElementById('googlepaycontainer')?.classList.remove("buy-with-gpay-btn");
                }
            })
            .catch(function (err: any) {
                // show error in developer console for debugging
                console.error(err);
            });
    }

    public addGooglePayButton() {
        const paymentsClient = this.getGooglePaymentsClient();
        const button =
            paymentsClient.createButton({
              onClick: () => this.onGooglePaymentButtonClicked(),
                allowedPaymentMethods: [this.getGooglePayPaymentMethod()]
            });
        document.getElementById('googlepaycontainer')?.appendChild(button);
        const button2 =
            paymentsClient.createButton({
                onClick: () => this.onGooglePaymentButtonClicked(),
                allowedPaymentMethods: [this.getGooglePayPaymentMethod()],
                buttonSizeMode: 'fill'
            });
        document.getElementById('googlepaycontainer2')?.appendChild(button2);
    }

    public getGoogleTransactionInfo(): google.payments.api.TransactionInfo {
        return {
            countryCode: this.uiStrings.isoRegionCode.toUpperCase(),
            currencyCode: this.uiStrings.currencyCode.toUpperCase(),
            totalPriceStatus: 'FINAL',
            // set to cart total
            totalPrice: this.shoppingCartTotalAmountWithSurcharge().toFixed(2)
        };
    }

    public prefetchGooglePaymentData() {
        const paymentDataRequest = this.getGooglePaymentDataRequest();
        // transactionInfo must be set but does not affect cache
        paymentDataRequest.transactionInfo = {
            totalPriceStatus: 'NOT_CURRENTLY_KNOWN',
            currencyCode: this.uiStrings.currencyCode.toUpperCase()
        } as any;
        const paymentsClient = this.getGooglePaymentsClient();

        paymentsClient.prefetchPaymentData(paymentDataRequest);
    }

    public onGooglePaymentButtonClicked() {
      if (!this.form.bookingEmail.isValid()) {
        this.form.triggerEmailValidation();
        return;
      }
      if (!this.acknowledgedTerms()) {
        this.showTermsError();
        return;    
      }

      const paymentDataRequest = this.getGooglePaymentDataRequest();
      paymentDataRequest.transactionInfo = this.getGoogleTransactionInfo();

      const paymentsClient = this.getGooglePaymentsClient();
      paymentsClient.loadPaymentData(paymentDataRequest)
          .then((paymentData: google.payments.api.PaymentData) => {
              // handle the response
              
              this.processGooglePayment(paymentData);
          })
          .catch(() => (err: any) => {
              // show error in developer console for debugging
              console.error(err);
              var errorPayload = {
                  name: "GooglePay",
                  message: `ErrorMessage: ${JSON.stringify(err)}`,
                  stack: new Error().stack
              } as Error;
      
              this.dataService.LogJavascriptWarning(errorPayload);
              this.setErrorMessage(`Payment declined. An error occurred when processing your payment. Try again in a little bit or select a different payment method before trying again.`);
          });
    }

    public onApplePayButtonClick() {
      if (!this.form.bookingEmail.isValid()) {
        this.form.triggerEmailValidation();
        return;
      }
      if (!this.acknowledgedTerms()) {
        this.showTermsError();
        return;    
      }

      //move to create request
      const paymentRequest = {
          currencyCode: this.uiStrings.currencyCode.toUpperCase(),
          countryCode: this.uiStrings.isoRegionCode.toUpperCase(),
          total: {
              label: this.uiStrings.systemName,
              amount: this.shoppingCartTotalAmountWithSurcharge()
          },
          supportedNetworks: this.allowedCardNetworks,
          merchantCapabilities: ['supports3DS'],
          //requiredBillingContactFields: ['email', 'name', 'phone', 'postalAddress'],
          requiredShippingContactFields: ['postalAddress', 'name', 'phone', 'email']
      };

      var session = new (window as any).ApplePaySession((window as any).global.config.applePayApiVersion, paymentRequest); //using lowest possible version

      // Merchant Validation
      session.onvalidatemerchant = (event: any) => {
          this.dataService.GetFatZebraApplePaySession(this.googleApplePaymentGatewayId(), event.validationURL)
              .then((result: any) => {
                  session.completeMerchantValidation(result);
              })
              .catch(function (err: any) {
                  // show error in developer console for debugging
                  console.error(err);
                  var errorPayload = {
                      name: "GetApplePaySession",
                      message: `ErrorMessage: ${JSON.stringify(err)}`,
                      stack: new Error().stack
                  } as Error;
          
                  this.dataService.LogJavascriptWarning(errorPayload);
                  this.setErrorMessage(`Payment declined. An error occurred when processing your payment. Try again in a little bit or select a different payment method before trying again.`);
              });
      };
      
      session.onpaymentauthorized = (event: any) => {
          this.processPaymentForApplePay(event, session);
      }
    
      session.oncancel = (event: any) => {
          console.error(event);
          var errorPayload = {
              name: "ApplePay Cancelled",
              message: `ErrorMessage: ${JSON.stringify(event)}`,
              stack: new Error().stack
          } as Error;
  
          this.dataService.LogJavascriptWarning(errorPayload);
          if (!this.isLoading()) {
              this.setErrorMessage(`Payment cancelled. Try again in a little bit or select a different payment method before trying again.`);
          }
      }
      
      session.begin();
    }

    private getUserDetailsForProcessing() {
      const userDetails = this.form.getUserDetails();

      this.form.rememberUserDetails(userDetails, this.accountId());

      return userDetails;
    }

    private async processPaymentForApplePay(paymentData: any, session: any) {
        this.isLoading(true);
        this.disablePurchase(true);

      this.paymentGatewayId(this.googleApplePaymentGatewayId());
        
      const creditCard: CreditCardModel = {
        cardToken: JSON.stringify(paymentData.payment.token),
        last4Digits: paymentData.payment.token.paymentMethod.displayName.substring(paymentData.payment.token.paymentMethod.displayName.length - 4),
        cardBrand: paymentData.payment.token.paymentMethod.network
      };
      const recaptcha = await this.getRecaptcha();

      const payload: CheckoutPayload = {
        paymentTypeName: creditCard.cardBrand,
        trackingTag: this.trackingTag(),
        customId: this.customId(),
        hasPermissionToContact: this.form.hasPermissionToContact(),
        paymentGatewayId: this.paymentGatewayId(),
        paymentRequestType: 'Apple Pay',
        creditCard: creditCard,
        recaptchaToken: recaptcha
      };

      const shoppingCart = this.shoppingCartVm();
      let billingAddress = paymentData.payment.shippingContact;

      const userDetails: UserBookingDetailsModel = {
        ...this.getUserDetailsForProcessing(),
        firstName: billingAddress.givenName,
        lastName: billingAddress.familyName,
        payerEmail: billingAddress.emailAddress,
        phoneNumber: billingAddress.phoneNumber,

        address: billingAddress.addressLines[0],
        city: billingAddress.locality,
        state: billingAddress.administrativeArea,
        postCode: billingAddress.postalCode
      };

      this.dataService.ProcessBooking(shoppingCart, userDetails, payload).done((result: { bookingUrlId: string }) => {
        session.completePayment((window as any).ApplePaySession.STATUS_SUCCESS);
        this.redirectToConfirmation(result);
      })
      .fail((result: any) => {
        if (result.errorMessage) {
          this.setErrorMessage(result.errorMessage);
          if (result.resultCode === 2) {
              if (this.shoppingCartVm().tickets) {
                  window.location.href = `${this.envSetting.eventSitePath}/${this.shoppingCartVm().tickets[0].event.id}/sessions`;
              }
          }
          ($('#foreign-card-surcharge-modal') as any).modal('hide');
        }
          session.completePayment((window as any).ApplePaySession.STATUS_FAILURE);
          this.dataService.LogJavascriptWarning({
              message: '[callProcessPayment] ' + result.responseJSON.errorMessage,
              name: '[callProcessPayment] ' + result.responseJSON.errorMessage,
              stack: new Error().stack
          } as Error);

          if (result.status === 400 && result.responseJSON.errorMessage) {
              ($('#foreign-card-surcharge-modal') as any).modal('hide');
              this.setErrorMessage(result.responseJSON.errorMessage);
              return;
          }

          var errorUrl = this.urlService.GetCheckoutErrorUrl(this.checkout.isWhiteLabel, this.checkout.isWidget);
          window.location.href = errorUrl;
      });
  
    }

    public async processGooglePayment(paymentData: google.payments.api.PaymentData) {
      // todo pass payment token to your gateway to process payment
      // paymentToken = paymentData.paymentMethodData.tokenizationData.token;
        this.isLoading(true);
        this.disablePurchase(true);

      this.paymentGatewayId(this.googleApplePaymentGatewayId());

      const creditCard: CreditCardModel = {
        cardToken: paymentData.paymentMethodData.tokenizationData.token,
        last4Digits: paymentData.paymentMethodData.info?.cardDetails,
        cardBrand: paymentData.paymentMethodData.info?.cardNetwork!
      };

      const recaptcha = await this.getRecaptcha();

      const payload: CheckoutPayload = {
        paymentTypeName: creditCard.cardBrand,
        trackingTag: this.trackingTag(),
        customId: this.customId(),
        hasPermissionToContact: this.form.hasPermissionToContact(),
        paymentGatewayId: this.paymentGatewayId(),
        paymentRequestType: 'Google Pay',
        creditCard: creditCard,
        recaptchaToken: recaptcha
      };

      const billingAddress = paymentData.paymentMethodData.info.billingAddress;
      const shoppingCart = this.shoppingCartVm();
      const name = billingAddress.name;
      const userDetails: UserBookingDetailsModel = {
        ...this.getUserDetailsForProcessing(),
        firstName: name.substring(0, name.indexOf(' ')),
        lastName: name.substring(name.indexOf(' ') + 1),
        payerEmail: paymentData.email,
        phoneNumber: billingAddress.phoneNumber,
        address: billingAddress.address1,
        city: billingAddress.locality,
        postCode: billingAddress.postalCode
      };


      this.dataService.ProcessBooking(shoppingCart, userDetails, payload)
      .done((result: { bookingUrlId: string}) => this.redirectToConfirmation(result))
      .fail((result: any) => {
        if (result.errorMessage) {
          this.setErrorMessage(result.errorMessage);
          if (result.resultCode === 2) {
              if (this.shoppingCartVm().tickets) {
                  window.location.href = `${this.envSetting.eventSitePath}/${this.shoppingCartVm().tickets[0].event.id}/sessions`;
              }
            }
            ($('#foreign-card-surcharge-modal') as any).modal('hide');
        }
          this.dataService.LogJavascriptWarning({
              message: '[callProcessPayment] ' + result.message,
              name: '[callProcessPayment] ' + result.name,
              stack: new Error().stack
          } as Error);

          if (result.status === 400 && result.responseJSON.errorMessage) {
              ($('#foreign-card-surcharge-modal') as any).modal('hide');
              this.setErrorMessage(result.responseJSON.errorMessage);
              return;
          }

          var errorUrl = this.urlService.GetCheckoutErrorUrl(this.checkout.isWhiteLabel, this.checkout.isWidget);
          window.location.href = errorUrl;
      });
    }

    private async processStripePayment(cardToken: string, ev: stripe.paymentRequest.StripePaymentMethodPaymentResponse) {
      // NAME WORKAROUND
      const names = ev.payerName.split(' ');
      let firstName = names[0];
      let lastName = '';

      for (let i = 1; i < names.length; i++) {
          lastName += names[i] + ' ';
      }

      lastName = lastName.trim();

      const userDetails = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        emailAddress: this.form.bookingEmail(),
        payerEmail: ev.payerEmail,
        phoneNumber: ev.paymentMethod.billing_details.phone,              
        address: ev.paymentMethod.billing_details.address?.line1,
        state: ev.paymentMethod.billing_details.address?.state,
        postCode: ev.paymentMethod.billing_details.address?.postal_code,
        countryCode: ev.paymentMethod.billing_details.address?.country,
        city: ev.paymentMethod.billing_details.address?.city,              
        isQuickSale: false,
      };

      const recaptcha = await this.getRecaptcha();

      const payload: CheckoutPayload = {
        paymentTypeName: ev.paymentMethod.card.brand.toLowerCase(),
        creditCard: {
          cardToken: cardToken,
          cardBrand: ev.paymentMethod.card.brand,
          cardCountry: ev.paymentMethod.card.country
        },
        hasPermissionToContact: this.form.hasPermissionToContact(),
        paymentRequestType: this._paymentRequestType,
        paymentGatewayId: this.paymentGatewayId(),
        isStripePaymentIntentBooking: (this.googleApplePaymentGatewayApiTypeName() === PAYMENT_GATEWAY.STRIPE || this.googleApplePaymentGatewayApiTypeName() === PAYMENT_GATEWAY.STRIPE_CONNECT) && this.config.config.stripeSCAEnabled,
        customId: this.customId(),
        recaptchaToken: recaptcha
      };
      
      this.dataService.ProcessBooking(this.shoppingCartVm(), userDetails, payload)
        .done((result: any) => {
            if (this.shoppingCartTotalAmount() === 0 || !payload.isStripePaymentIntentBooking) {
              ev.complete('success');
              this.redirectToConfirmation(result);
            }
            else {
                this.stripe.wallet.stripe.confirmCardPayment(result.paymentIntentClientSecret,
                    { payment_method: ev.paymentMethod.id },
                    { handleActions: false }).then((confirmResult: any) => {
                        if (confirmResult.error) {
                            processFailedPaymentWithNoAuthentication(ev, payload, confirmResult, result, userDetails);
                        }
                        else {
                            ev.complete('success');
                            if (confirmResult.paymentIntent.status === "requires_action" || confirmResult.paymentIntent.status.startsWith("requires_source_action")) {
                                processPaymentWithAuthentication(result, payload, userDetails, ev);
                            }
                            else {
                                processSuccessfulPaymentWithoutAuthentication(payload, result, confirmResult, userDetails, ev);
                            }
                        }
                    }).catch((error: stripe.Error) => this.StripeErrorHandler('ConfirmCardPaymentError', error, result.bookingUrlId));
            }
        })
        .fail((secondStageFailedResult: any) => {
          if (secondStageFailedResult.errorMessage) {
            ev.complete('success');
            this.setErrorMessage(secondStageFailedResult.errorMessage);
        }

            if (secondStageFailedResult.responseJSON.errorMessage) {
                ev.complete('success');
                this.setErrorMessage(secondStageFailedResult.responseJSON.errorMessage);
            }
            else {
                ev.complete('fail');
                this.setErrorMessage(`Payment declined. An error occurred when processing your payment. Try again in a little bit or select a different payment method before trying again.`);
            }
        });

      const processFailedPaymentWithNoAuthentication = (ev: stripe.paymentRequest.StripePaymentMethodPaymentResponse,
          payload: CheckoutPayload, confirmResult: stripe.PaymentIntentResponse, result: any, userDetails: UserBookingDetailsModel) => {
          ev.complete('success');
          payload.paymentIntentClientSecret = confirmResult.error.payment_intent.client_secret;
          payload.paymentIntentStatus = confirmResult.error.payment_intent.status;
          payload.bookingUrlId = result.bookingUrlId;

          this.dataService.ProcessBooking(this.shoppingCartVm(), userDetails, payload)
              .done(() => {
                  this.setErrorMessage(confirmResult.error.message);
                  return;
              })
              .fail((secondStageFailedResult: any) => {
                  if (secondStageFailedResult.responseJSON.errorMessage)
                      this.setErrorMessage(secondStageFailedResult.responseJSON.errorMessage);

                  else
                      this.setErrorMessage(confirmResult.error.message);
              });
      };

      const processPaymentWithAuthentication = (result: any,
        payload: CheckoutPayload, userDetails: UserBookingDetailsModel, ev: stripe.paymentRequest.StripePaymentMethodPaymentResponse)  => {
        this.stripe.wallet.stripe.confirmCardPayment(result.paymentIntentClientSecret).then((paymentResult: any) =>  {
            if (paymentResult.error) {
                payload.paymentIntentClientSecret = paymentResult.error.payment_intent.client_secret;
                payload.paymentIntentStatus = paymentResult.error.payment_intent.status;
                payload.bookingUrlId = result.bookingUrlId;

                this.dataService.ProcessBooking(this.shoppingCartVm(), userDetails, payload)
                    .done(x => this.redirectToConfirmation(x))
                    .fail((secondStageFailedResult: any) => {
                        if (secondStageFailedResult.responseJSON.errorMessage)
                            this.setErrorMessage(secondStageFailedResult.responseJSON.errorMessage);

                        else
                            this.setErrorMessage(paymentResult.error.message);
                    });
            } else {
                payload.bookingUrlId = result.bookingUrlId;
                payload.paymentIntentStatus = paymentResult.paymentIntent.status;
                payload.paymentIntentClientSecret = paymentResult.paymentIntent.client_secret;
                
                this.dataService.ProcessBooking(this.shoppingCartVm(), userDetails, payload)
                    .done(() => {
                      ev.complete('success');
                      this.redirectToConfirmation(result);
                    })
                    .fail((secondStageFailedResult: any) => {
                        if (secondStageFailedResult.responseJSON.errorMessage)
                            this.setErrorMessage(secondStageFailedResult.responseJSON.errorMessage);
                    });
            }
        }).catch((error: stripe.Error) => this.StripeErrorHandler('ConfirmCardPaymentError', error));
      }

      const processSuccessfulPaymentWithoutAuthentication = (payload: CheckoutPayload, result: any, confirmResult: stripe.PaymentIntentResponse, userDetails: UserBookingDetailsModel, ev: stripe.paymentRequest.StripePaymentMethodPaymentResponse) => {
        payload.paymentIntentStatus = confirmResult.paymentIntent?.status;
        payload.paymentIntentClientSecret = confirmResult.paymentIntent?.client_secret;
        payload.bookingUrlId = result.bookingUrlId;

          this.dataService.ProcessBooking(this.shoppingCartVm()!, userDetails, payload)
              .done(() => {
                ev.complete('success');
                this.redirectToConfirmation(result);
              }).fail((secondStageFailedResult: any) => {
                  if (secondStageFailedResult.responseJSON.errorMessage)
                      this.setErrorMessage(secondStageFailedResult.responseJSON.errorMessage);
              });
      };
    }


    private redirectToConfirmation(result: { bookingUrlId: string }) {
        try {
            this.trackConversion(result.bookingUrlId);
            this.trackPurchase(); // If this step fails, it should not prevent the rest of the function from executing
        } catch (error) {
            this.dataService.LogJavascriptWarning({
                message: '[redirectToConfirmation] ' + error.message,
                name: '[redirectToConfirmation] ' + error.name,
                stack: new Error().stack
            } as Error);
        }
      const confirmationUrl = this.urlService.GetConfirmationPageUrl(result.bookingUrlId, this.widgetType, this.widgetEventId, this.isEventListingWidget, this.accountId(), this.checkout.isWhiteLabel);

      if ('parentIFrame' in window && this.isWidget) {
          (window as any).parentIFrame.sendMessage('TBWidgetScrollToTopInvoke');
      }
      window.location.href = confirmationUrl;
    }
    

    private ShowEventTermsModal() {
        let eventTandCDetails = '';
        const eventTerms = this.eventTerms();

        //TO DO: consider using es6 iterator
        $.each(eventTerms, (index: number, value: TermsAndConditionsModel) => {
          eventTandCDetails += '<h5>' + value.eventName + '</h5><p>' + value.trybookingTC + '</p>';
          eventTandCDetails += `<h5>Event ${this.config.uiStrings.organiserSpelling.toLowerCase()} terms and conditions</h5><p>` + value.eventOrganiserTC + '</p>';
        });

        $bootstrapHelper.ShowModal('Terms and Conditions', eventTandCDetails);
    }

    /**
     * Processes tracking for events
     * Loads scripts into code scope as necessary
     * 
     * @param forEvents
     */
    private sendCartTrackingDetails(forEvents: number[]) {
        if (!forEvents?.length) return;

        this.dataService.GetTrackingCodes(forEvents)
            .then((result) => {
                if (!result) return;

                const {
                    facebookPixelEventTrackingList,
                    googleAnalyticsEventTrackingList,
                    tikTokPixelEventTrackingList
                } = result;

                // Facebook vm init
                const fbViewCheckout = getEventTrackingCodesByAction(facebookPixelEventTrackingList, 'View Checkout');
                const fbSubmitPayment = getEventTrackingCodesByAction(facebookPixelEventTrackingList, 'Submit Payment');
                this.shouldFBTrackCheckoutView(fbViewCheckout.length > 0);
                this.shouldFBTrackSubmitPayment(fbSubmitPayment.length > 0);
                if (this.shouldFBTrackCheckoutView()) waitForFbq(() => fbq('track', 'AddToCart'));

                // TikTok vm init
                const ttViewCheckout = getEventTrackingCodesByAction(tikTokPixelEventTrackingList, 'View Checkout');
                const ttSubmitPayment = getEventTrackingCodesByAction(tikTokPixelEventTrackingList, 'Submit Payment');
                this.shouldTikTokTrackCheckoutView(ttViewCheckout.length > 0);
                this.shouldTikTokTrackSubmitPayment(ttSubmitPayment.length > 0);
                if (this.shouldTikTokTrackCheckoutView()) waitForTtq(() => ttq.track('AddToCart'));

                // Google Analytics vm init
                const gViewCheckout = getEventTrackingCodesByAction(googleAnalyticsEventTrackingList, 'View Checkout');
                const gSubmitPayment = getEventTrackingCodesByAction(googleAnalyticsEventTrackingList, 'Submit Payment');
                this.googleTrackingCodes(googleAnalyticsEventTrackingList);
                this.shouldGATrackCheckoutView(gViewCheckout.length > 0);
                this.shouldGATrackSubmitPayment(gSubmitPayment.length > 0);
            });
    }

    private trackPurchase() {
        if (this.shouldFBTrackSubmitPayment() && fbq && this.shoppingCartTotalAmount() > 0) {
            fbq('track', 'AddPaymentInfo');
        }

        if (this.shouldTikTokTrackSubmitPayment() && ttq && this.shoppingCartTotalAmount() > 0) {
            ttq.track('AddPaymentInfo');
        }

        //ga submit payment tracking
        if (this.shouldGATrackSubmitPayment()) {
            this.googleTrackingCodes().forEach(code => {
                const totalEventAmount = $cartHelper.GetEventTotalAmount(this.shoppingCartVm(), code.eventId);
                const gtagEvents = getEventTrackingCodesByAction(this.googleTrackingCodes(), 'Submit Payment');
                if (window.gtag && totalEventAmount > 0) {
                    gtagEvents.forEach((g: any) => {
                        window.gtag('config', g.trackingId, {
                            'page_path': '/book/paymentInfo'
                        });
                    });
                }
            });
        }
    }

    private trackConversion(bookingUrlId: string) {
        if (this.hasTickets()) {
            if (window.gtag) {
                window.gtag('event', 'purchase', {
                    'transaction_id': bookingUrlId,
                    'value': this.shoppingCartTotalAmount(),
                    'currency': this.uiStrings.currencyCode,
                    'items': this.shoppingCartVm().tickets.map((ticket) => {
                        return {
                            'id': ticket.event.id,
                            'name': ticket.event.name,
                            'category': 'Booking',
                            'quantity': ticket.quantity,
                            'price': ticket.totalPricePerTicketName
                        };
                    })
                });
            }
        }
    }

    private async processPaymentIntentPayment(bookingResult: any, data: CheckoutPayload) {
      try {
        const paymentIntentResult = await this.stripe.confirmPurchase(bookingResult.paymentIntentClientSecret);


        if (paymentIntentResult.error) {
          data.paymentIntentClientSecret = paymentIntentResult.error.payment_intent?.client_secret;
          data.paymentIntentStatus = paymentIntentResult.error.payment_intent?.status;
          data.bookingUrlId = bookingResult.bookingUrlId;

          const userDetails = this.getUserDetailsForProcessing();
          const shoppingCart = this.shoppingCartVm();

          this.dataService.ProcessBooking(shoppingCart, userDetails, data).done(() => {
            this.setErrorMessage(paymentIntentResult.error.message);
          })
          .fail((secondStageFailedResult: any) => {

              if (secondStageFailedResult.responseJSON.errorMessage)
              this.setErrorMessage(secondStageFailedResult.responseJSON.errorMessage);
              else
              this.setErrorMessage(paymentIntentResult.error.message);
          });

        } else if (this.shoppingCartTotalAmount() === 0) {
          this.redirectToConfirmation(bookingResult);
        } else {
          data.bookingUrlId = bookingResult.bookingUrlId;
          data.paymentIntentStatus = paymentIntentResult.paymentIntent.status;
          data.paymentIntentClientSecret = paymentIntentResult.paymentIntent.client_secret;

          const userDetails = this.getUserDetailsForProcessing();
          const shoppingCart = this.shoppingCartVm();

          this.dataService.ProcessBooking(shoppingCart, userDetails, data)
            .done(() => this.redirectToConfirmation(bookingResult))
            .fail((result: any) => {
                this.dataService.LogJavascriptWarning({
                    message: '[callProcessPayment] ' + result.message,
                    name: '[callProcessPayment] ' + result.name,
                    stack: new Error().stack
                } as Error);

                if (result.status === 400 && result.responseJSON.errorMessage) {
                    this.showErrorMessage(true);
                    this.errorMessage(result.responseJSON.errorMessage);
                    this.isLoading(false);
                    this.disablePurchase(false);

                    return;
                }
                var errorUrl = this.urlService.GetCheckoutErrorUrl(this.checkout.isWhiteLabel, this.checkout.isWidget);
                window.location.href = errorUrl;
            });
        }
        ($('#foreign-card-surcharge-modal') as any).modal('hide');
      }
      catch (error: unknown) {
        this.StripeErrorHandler('ConfirmCardPaymentError', error as stripe.Error, bookingResult.bookingUrlId);
      }
    }

    private setErrorMessage(errorMessage: string) {
        this.showErrorMessage(true);
        this.errorMessage(errorMessage);
        this.isLoading(false);
        this.disablePurchase(false);

        this.ScrollToErrorMessage();
    }
    
    private ScrollToErrorMessage() {
        if (window.self === window.top) {
            $(window).scrollTop($("#checkout-error-message").offset().top - 20);
        }
        if ('parentIFrame' in window && this.isWidget) {
            (window as any).parentIFrame.sendMessage('TBWidgetScrollToTopInvoke');
        }
    }


    private updateCardBrand(cardBrand: string, cardNumberId: string, cardBrandId: string, surchargeId: string) {
      const brand = cardBrand.toLowerCase();

      if (this.cardBrand !== brand){
        this.cardBrand = brand;
        this.updateCreditCardLogo(brand, cardNumberId, cardBrandId);
        this.updateCardSurcharge(brand, surchargeId);
      }
    }

    private updateCreditCardLogo(brand: string, cardNumber: string, cardBrand: string) {
      const $cardNumberElement = $(cardNumber);
      const $cardBrandElement = $(cardBrand);

      switch (brand) {
        case 'visa':
          $cardNumberElement.addClass('card-identified');
          $cardBrandElement.show();
          $cardBrandElement.attr('src', '/media/imgs/9a3ef8e51fc5ab29d0673dab2dc61dea9e2c00bc.png');
          break;
        case 'mastercard':
          $cardNumberElement.addClass('card-identified');
          $cardBrandElement.show();
          $cardBrandElement.attr('src', '/media/imgs/2eadb366ced458c29390dacc21d5ed118b0ddb72.png');
          break;
        case 'amex':
          $cardNumberElement.addClass('card-identified');
          $cardBrandElement.show();
          $cardBrandElement.attr('src', '/media/imgs/a7bd620711d3ad0d0875c10b9f57b153e682fca1.png');
          break;
        default:
          $cardNumberElement.removeClass('card-identified');
          $cardBrandElement.hide();
          break;
      }
    }

    private updateCardSurcharge(cardBrand: string, surchargeInfoElement: string) {
      const brand = cardBrand.toLowerCase();

      const surcharge = this.surcharges().find(x => x.paymentTypeName?.toLowerCase() === brand);

      if (surcharge) {
        this.surchargeApplied(surcharge.percentage);

        $(surchargeInfoElement).addClass('d-flex'); // TO DO: Convert to KnockoutJS

        if (cardBrand.toLowerCase() === 'amex') {
            this.surchargeCardType('American Express');
        } else {
            this.surchargeCardType(cardBrand);
        }
      } else {
          this.surchargeApplied(0);
          $(surchargeInfoElement).removeClass('d-flex'); // TO DO: Convert to KnockoutJS
      }
    }

    private updateGooglePaySurcharge() {
      this.cardBrand = null; 
      const gpaySurcharge = this.surcharges().find(x => x.feeTypeId === FeeType.GooglePaySurcharge);
      if (gpaySurcharge) {
        this.surchargeApplied(gpaySurcharge.percentage);
      }
    }

    private updateApplePaySurcharge() {
      this.cardBrand = null; 
      const applePaySurcharge = this.surcharges().find(x => x.feeTypeId === FeeType.ApplePaySurcharge);
      if (applePaySurcharge) {
        this.surchargeApplied(applePaySurcharge.percentage);
      }
    }

    private showTermsError() {
      this.hasTermsError(true);
      this.termsErrorMessage('Agreeing to the terms and conditions is a precondition of using ' + this.uiStrings.systemName);
    }

    private hideTermsError() {
      this.hasTermsError(false);
      this.termsErrorMessage('');
    }

    public onSelectPayWithCreditCard() {
      // clear ga surcharge if switched tabs
      if (!$('#cc-btn').hasClass('active')) {
        this.surchargeApplied(0);
      }
      $('#cc-btn').addClass('active'); // To DO: convert to knockout
      this.isPayWithGoogleSelected(false);
      this.isPayWithApplePaySelected(false);
      this.isPayWithCreditCardSelected(true);
    }

    public onSelectPayWithGoogle() {
        $('#cc-btn').removeClass('active'); // To DO: convert to knockout
        this.isPayWithCreditCardSelected(false);
        this.isPayWithApplePaySelected(false);
        this.isPayWithGoogleSelected(true);
        this.updateGooglePaySurcharge();
    }

    public onSelectPayWithApplePay() {
        $('#cc-btn').removeClass('active'); // To DO: convert to knockout
        this.isPayWithGoogleSelected(false);
        this.isPayWithCreditCardSelected(false);
        this.isPayWithApplePaySelected(true);
        this.updateApplePaySurcharge();
    }
    

    public showEventTerms() {
        const eventIds = [...new Set(this.shoppingCartVm()?.tickets.map(x => x.event.id))];
        
        if (eventIds.length > 0) {
          if (this.eventTerms().length !== eventIds.length) {
            this.dataService.GetEventsTAndC(eventIds).then((data: TermsAndConditionsModel[]) => {
                  this.eventTerms(data);
                  this.ShowEventTermsModal();
                });
          }
          else {
              this.ShowEventTermsModal();
          }
        }
    }

    public purchase() {
        if (!this.acknowledgedTerms()) {
            this.showTermsError();
            return;
        }        
        if (!this.isDataCollectionComplete() || !this.cardPaymentTypeAllowed()) {
          this.setErrorMessage('One or more fields have invalid values. Please check and try again.');
          return;
        }
        if (!this.form.isValid()) {
          this.form.triggerValidation();
          return;
        }
        this.hideTermsError();
        this.isLoading(true);
        this.disablePurchase(true);

        this.paymentGatewayId(this.cardPaymentGatewayId());

        if (this.shoppingCartTotalAmount() === 0) {
          return this.processPayment('Free');
        }
        
        switch (this.cardPaymentGatewayApiTypeName()) {
          case PAYMENT_GATEWAY.FATZEBRA:
            return this.fatZebra.purchase();
          case PAYMENT_GATEWAY.CONNEXPOINT:
            return this.connexPoint.purchase();
          case PAYMENT_GATEWAY.TEST:
            return this.test.payment();
          case PAYMENT_GATEWAY.STRIPE:
          case PAYMENT_GATEWAY.STRIPE_CONNECT:
            return this.stripe.purchase();
          default: // should never get here.... 
            console.error("Invalid payment gateway");
            this.setErrorMessage('Invalid payment method.');
        }
    }

    private StripeErrorHandler(errorName: string, error: stripe.Error, bookingUrlId: string = ''): any {
        var errorPayload = {
            name: errorName,
            message: `ErrorMessage: ${error.message}, BookingUrlId=${bookingUrlId} DeclineCode: ${error.decline_code}, Type: ${error.type}`,
            stack: new Error().stack
        } as Error;

        this.dataService.LogJavascriptWarning(errorPayload);
        this.setErrorMessage(error.message);
    }

    public back() {
        this.scrollToTop();
        const refererNoTrailingSlash = document.referrer.replace(/\/$/, "");
        // bookingSitePath & eventSitePath checks added for local dev
        const isRefererSameSite = (document.referrer.split('/')[2] === window.location.host || refererNoTrailingSlash === this.envSetting.bookingSitePath || refererNoTrailingSlash === this.envSetting.eventSitePath.replace('/events', ''))

        if (isRefererSameSite) {
            history.go(-1);
        } else {
            if (this.shoppingCartVm().tickets) {
                window.location.href = this.envSetting.eventSitePath + '/landing/' + this.shoppingCartLastEventId();
            } else {
                window.location.href = (this.links() as any).buyGiftCertificateLink;
            }
        }
    }

    private scrollToTop() {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;

        if ('parentIFrame' in window) {
            (window as any).parentIFrame.sendMessage('TBWidgetScrollToTopInvoke');
        }
    }

    public onBuyWithGpayClick() {
      if (!this.form.bookingEmail.isValid()) {
        this.form.triggerEmailValidation();
        return;
      }
      if (!this.acknowledgedTerms()) {
        this.showTermsError();
        return;    
      }
      this.hideTermsError();
      this._paymentRequestType = 'Google Pay';
      this.stripe.wallet.showPaymentRequestSheet();
      this.paymentGatewayId(this.googleApplePaymentGatewayId());
    };

    public onCheckoutWithApplePay() {
      if (!this.form.bookingEmail.isValid()) {
        this.form.triggerEmailValidation();
        return;
      }
      if (!this.acknowledgedTerms()) {
        this.showTermsError();
        return;    
      }
      
      this.paymentGatewayId(this.googleApplePaymentGatewayId());
      if ((window as any).ApplePaySession && this.googleApplePaymentGatewayApiTypeName() === PAYMENT_GATEWAY.FATZEBRA) {
          this.onApplePayButtonClick();
      } else {
          this._paymentRequestType = 'Apple Pay';
          this.stripe.wallet.showPaymentRequestSheet();
      }
    };

    
  public onNoCartItems(eventId?: number, redirectToGc?: boolean) {
      var redirectUrl = this.urlService.GetNoCartItemsRedirectUrl(eventId, redirectToGc, this.isEventListingWidget, this.widgetType, this.accountId());
      window.location.href = redirectUrl;
  };


  // Called from foreign surcharge modal
  public continueProcessBooking() {
      this.isLoading(true);
      this.disablePurchase(true);

    this.processPayment(this.creditCard.cardBrand, { creditCard: this.creditCard});
  };

  private async processCardPayment(card: CreditCardModel): Promise<void> {
    const isForeignCard = this.isForeignCardSurchargeEnabled && !(this.domesticCardCountries.some(x => x === card.cardCountry));

    if (isForeignCard) {
      const foreignSurcharge = this.getForeignSurcharges(card);

      if (foreignSurcharge > 0) {
        this.creditCard = card;
          this.isLoading(false);
          this.disablePurchase(false);

        this.totalFCS($numberHelper.ToCurrency(foreignSurcharge));

        const totalAmountIncFcs = this.shoppingCartTotalAmount() + foreignSurcharge;
        this.totalAmountIncludingFCS($numberHelper.ToCurrency(totalAmountIncFcs));

        // TO DO: consider moving to a separate function
        ($('#foreign-card-surcharge-modal') as any)
            .modal({
                keyboard: false,
                show: true
            });
        if ('parentIFrame' in window) {
            (window as any).parentIFrame.sendMessage('TBWidgetScrollToTopInvoke');
        }
        return;
      }
    }
    this._paymentRequestType = undefined;

    await this.processPayment(card.cardBrand, { creditCard: card });
  }

  private async processPayment(paymentType: string, partialPayload?: Partial<CheckoutPayload>, userDetails?: UserBookingDetailsModel) {
    const shoppingCart = this.shoppingCartVm();

    try {
      userDetails = userDetails ?? this.getUserDetailsForProcessing();
      
      const recaptcha = await this.getRecaptcha();
      const payload: CheckoutPayload = {
        ...partialPayload,
        paymentGatewayId: this.paymentGatewayId(),
        paymentTypeName: paymentType,
        paymentRequestType: this._paymentRequestType,
        hasPermissionToContact: this.form.hasPermissionToContact(),
        trackingTag: this.trackingTag(),
        customId: this.customId(),
        isStripePaymentIntentBooking: (this.cardPaymentGatewayApiTypeName() === PAYMENT_GATEWAY.STRIPE || this.cardPaymentGatewayApiTypeName() === PAYMENT_GATEWAY.STRIPE_CONNECT) && this.config.config.stripeSCAEnabled,
        recaptchaToken: recaptcha
      };

      const result = await this.dataService.ProcessBooking(shoppingCart!, userDetails, payload)

      if (payload.isStripePaymentIntentBooking && this.shoppingCartTotalAmount() !== 0 && result.duplicateAbort !== true) {
        await this.processPaymentIntentPayment(result, payload);
      }
      else {
        this.redirectToConfirmation(result);
      }    
    } catch (error) {
      ($('#foreign-card-surcharge-modal') as any).modal('hide');

      if (error.errorMessage) {
        this.setErrorMessage(error.errorMessage);
        if (error.resultCode === 2) {
            if (shoppingCart?.tickets) {
                window.location.href = `${this.envSetting.eventSitePath}/${shoppingCart!.tickets[0].event.id}/sessions`;
            }
        }
      }

      this.dataService.LogJavascriptWarning({
          message: '[callProcessPayment] ' + error.message,
          name: '[callProcessPayment] ' + error.name,
          stack: new Error().stack
      } as Error);

      if (error.status === 400 && error.responseJSON.errorMessage) {
          ($('#foreign-card-surcharge-modal') as any).modal('hide');
          this.setErrorMessage(error.responseJSON.errorMessage);
          return;
      }
      var errorUrl = this.urlService.GetCheckoutErrorUrl(this.checkout.isWhiteLabel, this.checkout.isWidget);
      window.location.href = errorUrl;
    }     
  }

  private getRecaptcha(): Promise<string> {
    const isEnabled = (!this.recaptchaOnBookingsAlwaysOff() && !this.isRecaptchaOffOlderAccount()) && (this.requireRecaptchaOnFreeBookings() || this.requireRecaptchaOnPaidBookings());
    
    const promise = new Promise<string>((resolve, _) => {
      if (isEnabled && grecaptcha) {
        grecaptcha.ready(async () => {
          const token = await grecaptcha.execute(this.config.config.googleReCaptchaSiteKey, { action: 'submit' });
          resolve(token);
        });
      } else {
        resolve('');
      }
    });

    return promise;
  }

  private initRecaptcha() {
    const gRecaptchaUrl = `https://www.google.com/recaptcha/api.js?render=${this.config.config.googleReCaptchaSiteKey}`;
    if ($('head script[src="' + gRecaptchaUrl + '"]').length == 0) {
        var head = document.getElementsByTagName('head')[0];
        var script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = gRecaptchaUrl;
        script.async = true;
        script.defer = true;
        head.appendChild(script);
    }
  }

  private getForeignSurcharges(card: CreditCardModel): number {
    // which gateway says american express?
    const cardBrand = card.cardBrand.toLowerCase() === 'american express' ? 'amex' : card.cardBrand.toLowerCase();
    
    const paymentType = this.paymentGatewayGroup?.cardPaymentGateway.paymentTypes?.find(x => x.name.toLowerCase() === cardBrand);

    if (paymentType && paymentType.foreignCardSurchargePercentage) {
        return this.shoppingCartTotalAmount() * (paymentType.foreignCardSurchargePercentage / 100);
    }
    return 0;
  }
  
  private initEmailCorrectionSuggestion() {
    const featureDisabled = !(window as any).global.config.emailDomainSuggestionsEnabled;
    if (featureDisabled) {
      return;
    }
    
    ko.pureComputed(this.form.bookingEmail)
      .extend({ rateLimit: { method: "notifyWhenChangesStop", timeout: 500 } })
      .subscribe((email: string) => {
        if (featureDisabled || email.indexOf('@') <= 0) {
          return;
        }

        const emailSplit = email.split('@');
        if (!emailSplit[1]) {
          return;
        }

        this.dataService.GetCorrection(emailSplit[1])
        .then((response: {correction: string}) => {
          const correctionDomain = response?.correction;
          if (!correctionDomain) {
            this.emailCorrection(null);
            return;
          }

          const newEmail = `${emailSplit[0]}@${correctionDomain}`;
          this.emailCorrection({ correction: newEmail, misspelling: email});
          ko.applyBindingsToNode(document.getElementById('update-email'), { click: () => this.updateEmail(newEmail) });
        })
        .catch((err: any) => {
          this.emailCorrection(null);

          if (err.status !== 404) {
            var errorPayload = {
              name: "Email Correction",
              message: `ErrorMessage: ${JSON.stringify(err)}`,
              stack: err.stack ?? new Error().stack
            } as Error;

            this.dataService.LogJavascriptWarning(errorPayload);
          }
        });
    });
  }

  private updateEmail = (correction: string) => {
    this.form.bookingEmail(correction);
    this.emailCorrection(null);

    $('.email-txt-container .zmdi-check-circle')
      .fadeIn(0)
      .delay(500)
      .fadeOut(400);
  }
}